import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiRequest } from '../../services/api';
import styles from './AccountSettings.module.css';

export default function AccountSettings() {
    const { user, updateUserData } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        display_name: ''
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [emailChanged, setEmailChanged] = useState(false);
    const [pendingEmail, setPendingEmail] = useState(null);
    const [currentPassword, setCurrentPassword] = useState('');

    useEffect(() => {
        if (user) {
            setFormData({
                username: user.username || '',
                email: user.email || '',
                display_name: user.display_name || user.displayName || ''
            });
        }
    }, [user]);

    const validateForm = () => {
        const newErrors = {};

        // Username validation
        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        } else if (!/^[a-z0-9_]{3,30}$/.test(formData.username.trim().toLowerCase())) {
            newErrors.username = '3-30 chars: lowercase letters, numbers, underscores only';
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email.trim())) {
            newErrors.email = 'Enter a valid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: undefined }));
        setSaved(false);
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setSaving(true);
        setSaved(false);
        setEmailChanged(false);

        try {
            const wasEmailChanged = formData.email.trim().toLowerCase() !== user.email;
            const data = await apiRequest('/account/me', {
                method: 'PATCH',
                body: {
                    username: formData.username.trim().toLowerCase(),
                    display_name: formData.display_name.trim(),
                    // An email change is held as pending until confirmed from
                    // this account, and needs the current password.
                    ...(wasEmailChanged ? { email: formData.email.trim().toLowerCase(), currentPassword } : {}),
                }
            });

            // Update user data in context (the account email is unchanged until confirmed)
            await updateUserData(data);
            if (wasEmailChanged) {
                setPendingEmail(data?.pending_email || formData.email.trim().toLowerCase());
                setFormData(prev => ({ ...prev, email: data?.email || user.email }));
                setCurrentPassword('');
            }

            setSaved(true);

            // Show email verification message if email was changed
            if (wasEmailChanged) {
                setEmailChanged(true);
            }

            setTimeout(() => {
                setSaved(false);
            }, 5000);

        } catch (error) {
            console.error('Error updating account:', error);

            if (error.code === 'current_password_incorrect' || error.code === 'current_password_required') {
                setErrors(prev => ({ ...prev, currentPassword: error.message }));
            } else if (error.message) {
                if (error.message.toLowerCase().includes('username')) {
                    setErrors(prev => ({ ...prev, username: error.message }));
                } else if (error.message.toLowerCase().includes('email')) {
                    setErrors(prev => ({ ...prev, email: error.message }));
                } else {
                    alert(error.message);
                }
            } else {
                alert('Something went wrong. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    const hasChanged = () => {
        if (!user) return false;
        return (
            formData.username.trim().toLowerCase() !== user.username ||
            formData.email.trim().toLowerCase() !== user.email ||
            formData.display_name.trim() !== (user.display_name || '')
        );
    };

    return (
        <main className={styles.page}>
            <div className={styles.inner}>
                {/* Screen header */}
                <div className={styles.screenHeader}>
                    <button
                        className={styles.backBtn}
                        onClick={() => navigate('/settings')}
                        title="Back to settings"
                    >
                        ← Back
                    </button>
                    <h1 className={styles.screenTitle}>Account Settings</h1>
                    <div className={styles.headerSpacer} />
                </div>

                <div className={styles.subtitle}>
                    Manage your username, email, and display name
                </div>

                {/* Email changed notification */}
                {emailChanged && (
                    <div className={styles.successBanner}>
                        <strong>Confirm your new email</strong>
                        <p>
                            We sent a link to {pendingEmail}. Open it while signed in to this account to make it
                            your email. Until then, {user?.email} stays your account email.
                        </p>
                    </div>
                )}

                {/* Form */}
                <div className={styles.formContainer}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Profile details</h2>
                        <p className={styles.sectionDescription}>These details appear throughout Aluvo.</p>
                    </div>

                    {/* Username field */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.label} htmlFor="username">Username</label>
                        <input
                            id="username"
                            type="text"
                            className={`${styles.input} ${errors.username ? styles.inputError : ''}`}
                            value={formData.username}
                            onChange={(e) => handleInputChange('username', e.target.value)}
                            placeholder="your_username"
                            autoCapitalize="none"
                            autoCorrect="off"
                        />
                        {errors.username ? (
                            <p className={styles.errorText}>{errors.username}</p>
                        ) : (
                            <p className={styles.hint}>
                                Lowercase letters, numbers, underscores — max 30 chars
                            </p>
                        )}
                    </div>

                    {/* Display Name field */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.label} htmlFor="display_name">Display Name</label>
                        <input
                            id="display_name"
                            type="text"
                            className={`${styles.input} ${errors.display_name ? styles.inputError : ''}`}
                            value={formData.display_name}
                            onChange={(e) => handleInputChange('display_name', e.target.value)}
                            placeholder="Your Display Name"
                            maxLength={100}
                        />
                        {errors.display_name && (
                            <p className={styles.errorText}>{errors.display_name}</p>
                        )}
                        <p className={styles.hint}>
                            This is how your name appears to others (max 100 chars)
                        </p>
                    </div>

                    {/* Email field */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.label} htmlFor="email">Email</label>
                        <input
                            id="email"
                            type="email"
                            className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="you@example.com"
                            autoCapitalize="none"
                            autoCorrect="off"
                        />
                        {errors.email && (
                            <p className={styles.errorText}>{errors.email}</p>
                        )}
                        <p className={styles.hint}>
                            If you change your email, you'll confirm it from a link we send to the new address
                        </p>
                    </div>

                    {/* Current password: required to change the email of an account that has one */}
                    {user && user.has_password !== false && formData.email.trim().toLowerCase() !== user.email && (
                        <div className={styles.fieldGroup}>
                            <label className={styles.label} htmlFor="currentPassword">Current password</label>
                            <input
                                id="currentPassword"
                                type="password"
                                autoComplete="current-password"
                                className={`${styles.input} ${errors.currentPassword ? styles.inputError : ''}`}
                                value={currentPassword}
                                onChange={(e) => { setCurrentPassword(e.target.value); setErrors(prev => ({ ...prev, currentPassword: undefined })); }}
                            />
                            {errors.currentPassword && (
                                <p className={styles.errorText}>{errors.currentPassword}</p>
                            )}
                        </div>
                    )}

                    {/* Save button */}
                    <button
                        className={`${styles.saveBtn} ${
                            saved ? styles.saveBtnSuccess : ''
                        } ${(!hasChanged() || saving) ? styles.saveBtnDisabled : ''}`}
                        onClick={handleSave}
                        disabled={!hasChanged() || saving}
                    >
                        {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </main>
    );
}
